// Lightweight combobox: button → dropdown with a search input and a
// filtered option list, optionally with a "+ Create new ..." footer.
//
// Built rather than adding a UI dep (no headlessui / cmdk / radix in
// the tree) and shaped specifically for the receipt-review surface,
// but generic over the option type so it can be reused for other
// per-row pickers. For free-text-with-suggestions (the vendor-name
// case) prefer `ManufacturingVendorAutocomplete` -- that one is a
// plain input + `<datalist>` because the value isn't bounded to a
// catalog.
import { useEffect, useMemo, useRef, useState } from "react";
import { matchesLooseCatalogQuery } from "@/lib/manufacturing/looseCatalogQuery";

export type ComboboxOption<T> = {
  key: string;
  label: string;
  sublabel?: string;
  data: T;
};

type CreateFnResult<T> =
  | { option: ComboboxOption<T>; error?: undefined }
  | { option?: undefined; error: string };

type CreateFn<T> = (query: string) => Promise<CreateFnResult<T>>;

type Props<T> = {
  value: string;
  options: ComboboxOption<T>[];
  onPick: (option: ComboboxOption<T>) => void;
  placeholder?: string;
  disabled?: boolean;
  /** When set, an inline "Create new ..." row appears under the
   *  options when the search query is non-empty and no option's label
   *  matches it exactly (case-insensitive). The callback is invoked
   *  with the raw query string; on success we auto-pick the returned
   *  option. */
  onCreate?: CreateFn<T>;
  /** Label for the create-new row. Defaults to "+ Create new". */
  createLabel?: string;
  /** Optional initial search query (e.g. the OCR line description to
   *  jumpstart the filter). */
  initialQuery?: string;
};

const triggerClass =
  "flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-left text-xs text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:disabled:bg-zinc-800 dark:disabled:text-zinc-400";

const searchInputClass =
  "w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";

const optionClass =
  "flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-xs hover:bg-indigo-50 disabled:opacity-50 dark:hover:bg-indigo-950/35";

export function ManufacturingComboboxPicker<T>({
  value,
  options,
  onPick,
  placeholder,
  disabled,
  onCreate,
  createLabel,
  initialQuery,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(initialQuery ?? "");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const selected = useMemo(
    () => options.find((o) => o.key === value) ?? null,
    [options, value]
  );

  const filtered = useMemo(() => filterComboboxOptions(options, query), [options, query]);

  const showCreate =
    !!onCreate &&
    query.trim().length > 0 &&
    !options.some((o) => o.label.toLowerCase() === query.trim().toLowerCase());

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setCreateError(null);
      window.setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  async function handleCreate() {
    if (!onCreate) return;
    const q = query.trim();
    if (!q) return;
    setCreating(true);
    setCreateError(null);
    const r = await onCreate(q);
    setCreating(false);
    if ("error" in r && r.error) {
      setCreateError(r.error);
      return;
    }
    if (r.option) {
      onPick(r.option);
      setOpen(false);
      setQuery("");
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={triggerClass}
        disabled={disabled}
        title={selected ? selected.label : undefined}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? "truncate" : "truncate text-gray-500 dark:text-zinc-400"}>
          {selected ? selected.label : placeholder ?? "— pick —"}
        </span>
        <span aria-hidden className="text-gray-400 dark:text-zinc-500">
          ▾
        </span>
      </button>
      {open ? (
        <div className="absolute left-0 right-0 z-10 mt-1 max-h-72 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
          <div className="border-b border-gray-100 p-1 dark:border-zinc-800">
            <input
              ref={searchRef}
              type="search"
              className={searchInputClass}
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                const first = filtered[0];
                if (e.key === "Enter" && first) {
                  e.preventDefault();
                  onPick(first);
                  setOpen(false);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
            />
          </div>
          <ul className="max-h-52 overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 ? (
              <li className="px-2 py-1 text-[11px] text-gray-500 dark:text-zinc-400">
                {query.trim()
                  ? `No matches for "${query.trim()}"`
                  : "No options."}
              </li>
            ) : (
              filtered.map((o) => (
                <li key={o.key}>
                  <button
                    type="button"
                    className={`${optionClass} ${o.key === value ? "bg-indigo-50/60 dark:bg-indigo-950/30" : ""}`}
                    title={o.label}
                    onClick={() => {
                      onPick(o);
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-zinc-100">
                      {o.label}
                    </span>
                    {o.sublabel ? (
                      <span className="shrink-0 text-[10px] text-gray-500 dark:text-zinc-400">
                        {o.sublabel}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))
            )}
          </ul>
          {showCreate ? (
            <div className="border-t border-gray-100 dark:border-zinc-800">
              <button
                type="button"
                className={`${optionClass} font-medium text-emerald-700 dark:text-emerald-300`}
                onClick={() => void handleCreate()}
                disabled={creating}
              >
                <span className="min-w-0 flex-1 truncate">
                  {creating
                    ? "Creating…"
                    : `${createLabel ?? "+ Create new"}: "${query.trim()}"`}
                </span>
              </button>
              {createError ? (
                <div className="px-2 pb-1.5 text-[11px] text-red-700 dark:text-red-300">
                  {createError}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Pure helper extracted so unit tests can pin filter semantics
 *  without rendering the component. Same matching rules as the
 *  in-component `filtered` memo above. */
export function filterComboboxOptions<T>(
  options: ComboboxOption<T>[],
  query: string
): ComboboxOption<T>[] {
  const q = query.trim();
  if (!q) return options;
  return options.filter((o) => matchesLooseCatalogQuery(`${o.label} ${o.sublabel ?? ""}`, q));
}
